import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'

import { ROW_PER_PAGE } from 'constants/common'
import { ISearchPrams, ISort } from 'models/common'
import { removeFalsyProperty } from 'utils/common'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

interface defaultRecord {
  id?: number | string
  [key: string]: any
}

interface IDefaultParamsProps<T extends defaultRecord, K extends ISearchPrams> {
  page?: number
  sort?: ISort
  sortBy?: keyof T
  rowsPerPage?: number
  search?: string
  extraParams?: Partial<K>
}

export interface ITableParams<T extends object = defaultRecord> {
  page: number
  setPage: Dispatch<SetStateAction<number>>
  sort: ISort
  setSort: Dispatch<SetStateAction<ISort>>
  rowsPerPage: number
  setRowsPerPage: Dispatch<SetStateAction<number>>
  sortBy: keyof T
  setSortBy: Dispatch<SetStateAction<keyof T>>
}

function useTableParams<T extends defaultRecord = defaultRecord, K extends ISearchPrams = ISearchPrams>(
  params?: IDefaultParamsProps<T, K>
) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { pathname } = useLocation()

  const queryPage = Number(searchParams?.get('page')) || 1

  const [page, setPage] = useState(queryPage || params?.page || 0)
  const [sort, setSort] = useState<ISort>((searchParams?.get('direction') as ISort) || params?.sort || 'desc')
  const [sortBy, setSortBy] = useState<keyof T>(searchParams?.get('sortBy') || params?.sortBy || 'createdAt')
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(searchParams?.get('limit')) || params?.rowsPerPage || ROW_PER_PAGE
  )
  const [search, setSearch] = useState<string>(searchParams?.get('search') || '')
  const [extraParams, setExtraParams] = useState<Partial<K> | undefined>(params?.extraParams)

  const [isInit, setIsInit] = useState(true)

  useEffect(() => {
    setPage(1)
  }, [rowsPerPage, search])

  useEffect(() => {
    if (!isInit) {
      navigate(
        `${pathname}?${new URLSearchParams(
          removeFalsyProperty({
            limit: rowsPerPage,
            page: page,
            sortBy,
            search,
            direction: sort,
            ...extraParams
          }) as K
        ).toString()}`
      )
    } else {
      setIsInit(false)
      setSearchParams(
        `${new URLSearchParams(
          removeFalsyProperty({
            limit: rowsPerPage,
            page: page,
            sortBy,
            search,
            direction: sort,
            ...extraParams
          }) as K
        ).toString()}`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page, sort, extraParams, sortBy, search])

  const data = useMemo(() => {
    const tableParams: ITableParams<T> = {
      page,
      sort,
      sortBy,
      rowsPerPage,
      setPage,
      setSort,
      setSortBy,
      setRowsPerPage
    }

    return {
      tableParams,
      searchParams: removeFalsyProperty({
        limit: rowsPerPage,
        sortBy,
        page: page,
        search,
        direction: sort,
        ...extraParams
      }) as K,
      search,
      setSearch,
      setExtraParams
    }
  }, [rowsPerPage, page, sort, extraParams, sortBy, search])

  return data
}

export default useTableParams
